import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FileText, Printer, Pencil, Plus, Paperclip, Calculator, Save, Trash2, FileSpreadsheet } from "lucide-react";
import FormularioIT1 from "@/components/it1/FormularioIT1";
import FormularioAnexoA from "@/components/it1/FormularioAnexoA";
import Formulario606 from "@/components/it1/Formulario606";
import { recalcularIT1, recalcularAnexoA, formatPeriodo, fmtNum, MESES } from "@/components/it1/dgiiUtils";
import "@/components/it1/dgii-form.css";
import AxiosDB from "@/Config/AxiosDB";
import Formulario607 from "@/components/it1/Formulario607";




// Mapeo del reporte de Ventas (607) a las casillas del Anexo A por tipo de comprobante.
// { cant: casilla de cantidad, sub: casilla de subtotal/monto }
const MAP_607_ANEXO = {
  "31": { cant: "cas001", sub: "cas101" },
  "32": { cant: "cas002", sub: "cas102" },
  "33": { cant: "cas003", sub: "cas103" },
  "34": { cant: "cas004", sub: "cas104" },
  "44": { cant: "cas006", sub: "cas106" },
  "45": { cant: "cas007", sub: "cas107" },
  "46": { cant: "cas008", sub: "cas108" },
};

// Sección III (formas de venta): { campo del reporte 607 -> casilla Anexo A }
const PAGO_607_ANEXO = {
  efectivo: "cas112",
  cheque: "cas113",
  tarjeta: "cas114",
  venta_credito: "cas115",
  permuta: "cas116",
  nota_credito: "cas117",
  otra_forma_pago: "cas118",
};
// Tipos que suman en la Sección III; el 34 se resta; los demás se ignoran.
const PAGO_TIPOS_SUMA = new Set(["31", "32", "33", "44", "45", "46"]);


const aplicarVentas607AAnexo = (anexo, filas) => {
  const cantAcc = {};
  const subAcc = {};
  const pagoAcc = {};
  (filas || []).forEach((f) => {
    const t = f.tipo_comprobante;
    const m = MAP_607_ANEXO[t];
    if (m) {
      cantAcc[m.cant] = (cantAcc[m.cant] || 0) + (Number(f.cantidad) || 0);
      subAcc[m.sub] = (subAcc[m.sub] || 0) + (Number(f.subtotal) || 0);
    }
    // Sección III: (31+32+33+44+45+46) - 34, otros se ignoran
    let signo = 0;
    if (PAGO_TIPOS_SUMA.has(t)) signo = 1;
    else if (t === "34") signo = -1;
    if (signo !== 0) {
      Object.entries(PAGO_607_ANEXO).forEach(([campo, cas]) => {
        pagoAcc[cas] = (pagoAcc[cas] || 0) + signo * (Number(f[campo]) || 0);
      });
    }
  });
  const next = { ...anexo };
  Object.entries(cantAcc).forEach(([k, v]) => { next[k] = fmtNum(v); });
  Object.entries(subAcc).forEach(([k, v]) => { next[k] = fmtNum(v); });
  Object.entries(pagoAcc).forEach(([k, v]) => { next[k] = fmtNum(v); });
  return next;
};
// Sincroniza el IT-1 con valores que provienen del Anexo A y recalcula:
//   it1.cas001 = anexo.cas111 ; it1.cas022 = anexo.cas352
const syncIt1DesdeAnexo = (it1, anexo) => recalcularIT1({
  ...it1,
  cas001: anexo.cas111 !== undefined ? anexo.cas111 : it1.cas001,
  cas022: anexo.cas352 !== undefined ? anexo.cas352 : it1.cas022,
});
// 607 -> IT-1: cas011 = Gravado 18%, cas012 = Gravado 16%
// Regla: (31+32+33+44+45+46) - 34; otros tipos se ignoran.
const aplicarVentas607AIt1Base = (it1, filas) => {
  let g18 = 0;
  let g16 = 0;
  (filas || []).forEach((f) => {
    const t = f.tipo_comprobante;
    let signo = 0;
    if (PAGO_TIPOS_SUMA.has(t)) signo = 1;
    else if (t === "34") signo = -1;
    if (signo !== 0) {
      g18 += signo * (Number(f.gravado_18) || 0);
      g16 += signo * (Number(f.gravado_16) || 0);
    }
  });
  return { ...it1, cas011: fmtNum(g18), cas012: fmtNum(g16) };
};

export default function ReporteImpuesto({ user, onLogout }) {
  const currentYear = new Date().getFullYear();
  const [declaraciones, setDeclaraciones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Diálogo Nueva
  const [showNew, setShowNew] = useState(false);
  const [nuevoAno, setNuevoAno] = useState(currentYear.toString());
  const [nuevoMes, setNuevoMes] = useState((new Date().getMonth() + 1).toString());
  const [creando, setCreando] = useState(false);

  // Diálogo Editar / Imprimir / Anexo
  const [editData, setEditData] = useState(null); // declaración completa en edición
  const [printData, setPrintData] = useState(null); // { tipo: 'it1'|'anexo', decl }
  const [activeTab, setActiveTab] = useState("it1");
  const [guardando, setGuardando] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const anos = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const loadDeclaraciones = async () => {
    setLoading(true);
    try {
      const res = await AxiosDB.get(`${API}/declaraciones-it1`);
      setDeclaraciones(res.data);
    } catch (e) {
      toast.error("Error al cargar las declaraciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeclaraciones(); }, []);

  const handleCrear = async () => {
    setCreando(true);
    const periodo = `${nuevoAno}${nuevoMes.padStart(2, "0")}`;
    try {
      await AxiosDB.post(`${API}/declaraciones-it1`, { periodo });
      toast.success("Declaración creada");
      setShowNew(false);
      loadDeclaraciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la declaración");
    } finally {
      setCreando(false);
    }
  };

  const abrirEditar = async (id) => {
    try {
      const res = await AxiosDB.get(`${API}/declaraciones-it1/${id}`);
      setEditData(res.data);
      setActiveTab("it1");
    } catch (e) {
      toast.error("Error al abrir la declaración");
    }
  };

  const abrirImprimir = async (id, tipo) => {
    try {
      const res = await AxiosDB.get(`${API}/declaraciones-it1/${id}`);
      setPrintData({ tipo, decl: res.data });
    } catch (e) {
      toast.error("Error al abrir el documento");
    }
  };

  const handleGuardar = async () => {
    if (!editData) return;
    setGuardando(true);
    try {
      await AxiosDB.put(`${API}/declaraciones-it1/${editData.id}`, {
        it1: editData.it1,
        anexo_a: editData.anexo_a,
        compras_606: editData.compras_606 || {},
        estado: editData.estado,
      });
      toast.success("Declaración guardada exitosamente");
      setEditData(null);
      loadDeclaraciones();
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setGuardando(false);
    }
  };



  const handleEliminar = async () => {
    if (!deleteTarget) return;
    try {
      await AxiosDB.delete(`${API}/declaraciones-it1/${deleteTarget.id}`);
      toast.success("Declaración eliminada");
      setDeleteTarget(null);
      loadDeclaraciones();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // const handlePrint = () => window.print();

  const handlePrint = () => {
    const contenido = document.getElementById("printDocumentit1").innerHTML;

    const estilos = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
      .map((el) => el.outerHTML)
      .join("\n");

    const ventana = window.open("", "_blank", "width=900,height=700");

    ventana.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Imprimir</title>
            ${estilos}

            <style>
                /* Forzar tamaño de letra en toda la impresión */
                html, body, body * {
                    font-size: 10px !important;
                }

                body {
                    font-family: Arial, Helvetica, sans-serif;
                    margin: 0;
                    padding: 15px;
                    background: white;
                }

                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                td, th {
                    padding: 3px !important;
                }

                @media print {
                    body {
                        margin: 0;
                    }
                }
            </style>

        </head>

        <body>
            ${contenido}
        </body>

        </html>
    `);

    ventana.document.close();

    ventana.onload = () => {
      ventana.focus();
      ventana.print();
      // ventana.close();
    };

  };

  return (
    <Layout user={user} onLogout={onLogout} title="Reporte de Impuesto (IT-1)">
      <Card className="mb-6" data-testid="it1-header-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Declaraciones de ITBIS (IT-1)</CardTitle>
            <CardDescription>
              Listado de declaraciones por período. Edite, imprima el documento IT-1 o el Anexo A.
            </CardDescription>
          </div>
          <Button onClick={() => setShowNew(true)} data-testid="nueva-declaracion-button">
            <Plus className="w-4 h-4 mr-2" /> Nueva Declaración
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 py-8 text-center">Cargando...</p>
          ) : declaraciones.length === 0 ? (
            <div className="text-center py-12 text-slate-600" data-testid="it1-empty">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-lg">No hay declaraciones. Crea una nueva para comenzar.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <Table data-testid="it1-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Período</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Total General (RD$)</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {declaraciones.map((d) => (
                    <TableRow key={d.id} data-testid={`it1-row-${d.periodo}`}>
                      <TableCell className="font-medium">{formatPeriodo(d.periodo)}</TableCell>
                      <TableCell>
                        <Badge variant={d.estado === "presentada" ? "default" : "secondary"}
                          className={d.estado === "presentada" ? "bg-green-600" : ""}>
                          {d.estado === "presentada" ? "Presentada" : "Borrador"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtNum(d.total_general)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => abrirEditar(d.id)} data-testid={`editar-${d.periodo}`}>
                            <Pencil className="w-4 h-4 mr-1" /> Editar
                          </Button>
                          <Button size="sm" variant="outline" className="text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100"
                            onClick={() => abrirImprimir(d.id, "it1")} data-testid={`imprimir-${d.periodo}`}>
                            <Printer className="w-4 h-4 mr-1" /> Imprimir Documento
                          </Button>
                          <Button size="sm" variant="outline" className="text-purple-700 border-purple-200 bg-purple-50 hover:bg-purple-100"
                            onClick={() => abrirImprimir(d.id, "anexo")} data-testid={`anexo-${d.periodo}`}>
                            <Paperclip className="w-4 h-4 mr-1" /> Anexo
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteTarget(d)} data-testid={`eliminar-${d.periodo}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo Nueva Declaración */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="nueva-declaracion-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Declaración IT-1</DialogTitle>
            <DialogDescription>Selecciona el año y mes del período fiscal a declarar.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-4 py-4">
            <div className="flex-1">
              <Label>Año</Label>
              <Select value={nuevoAno} onValueChange={setNuevoAno}>
                <SelectTrigger data-testid="nuevo-ano"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anos.map((a) => <SelectItem key={a} value={a.toString()}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Mes</Label>
              <Select value={nuevoMes} onValueChange={setNuevoMes}>
                <SelectTrigger data-testid="nuevo-mes"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MESES.slice(1).map((m, i) => <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={handleCrear} disabled={creando} data-testid="confirmar-nueva">
              {creando ? "Creando..." : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Editar */}
      <Dialog open={!!editData} onOpenChange={(o) => !o && setEditData(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="editar-dialog">
          <DialogHeader className="dgii-no-print">
            <DialogTitle>Editar Declaración — {editData && formatPeriodo(editData.periodo)}</DialogTitle>
            <DialogDescription>Edita los campos del formulario IT-1 y del Anexo A. Usa "Recalcular Totales" para actualizar las sumas.</DialogDescription>
          </DialogHeader>
          {editData && (
            <>
              <div className="flex items-center justify-between gap-3 dgii-no-print">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Estado:</Label>
                  <Select value={editData.estado} onValueChange={(v) => setEditData({ ...editData, estado: v })}>
                    <SelectTrigger className="w-40" data-testid="estado-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="borrador">Borrador</SelectItem>
                      <SelectItem value="presentada">Presentada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleGuardar} disabled={guardando} data-testid="guardar-button">
                    <Save className="w-4 h-4 mr-2" /> {guardando ? "Guardando..." : "Guardar"}
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2" style={{ minHeight: '90vh', minWidth: '90vh' }}>
                <TabsList className="dgii-no-print">
                  <TabsTrigger value="it1" data-testid="tab-it1">Formulario IT-1</TabsTrigger>
                  <TabsTrigger value="anexo" data-testid="tab-anexo">Anexo A</TabsTrigger>
                  <TabsTrigger value="t606" data-testid="tab-606">606 Compras</TabsTrigger>
                  <TabsTrigger value="t607" data-testid="tab-607">Ventas (607)</TabsTrigger>
                </TabsList>
                <TabsContent value="it1">
                  <FormularioIT1 data={editData.it1} onChange={(it1) => setEditData({ ...editData, it1: recalcularIT1(it1) })} />

                </TabsContent>
                <TabsContent value="anexo">
                  <FormularioAnexoA data={editData.anexo_a}
                    onChange={(anexo_a) => {
                      const anexo = recalcularAnexoA(anexo_a);
                      // const it1 = anexo.cas352 !== undefined
                      //   ? recalcularIT1({ ...editData.it1, cas022: anexo.cas352 })
                      //   : editData.it1;
                      const it1 = syncIt1DesdeAnexo(editData.it1, anexo);

                      setEditData({ ...editData, anexo_a: anexo, it1 });

                      // onChange={(anexo_a) => setEditData({ ...editData, anexo_a: recalcularAnexoA(anexo_a) 
                    }}

                  />
                </TabsContent>
                <TabsContent value="t606">
                  {/* <Formulario606 data={editData.compras_606 || {}} onChange={(compras_606) => setEditData({ ...editData, compras_606 })} /> */}
                  <Formulario606
                    data={editData.compras_606 || {}}
                    onChange={(compras_606) => {
                      const next = { ...editData, compras_606 };
                      if (compras_606 && compras_606.totales) {
                        const adelantar = compras_606.totales.itbis_adelantar || 0;
                        const anexo = recalcularAnexoA({ ...editData.anexo_a, cas150: fmtNum(adelantar) });
                        next.anexo_a = anexo;
                        // if (anexo.cas352 !== undefined) {
                        //   next.it1 = recalcularIT1({ ...editData.it1, cas022: anexo.cas352 });
                        // }
                        next.it1 = syncIt1DesdeAnexo(editData.it1, anexo);

                        toast.info(`ITBIS por Adelantar (${fmtNum(adelantar)}) cargado en Anexo A casilla 150`);
                      }
                      setEditData(next);
                    }}
                  />
                </TabsContent>
                <TabsContent value="t607">
                  <Formulario607
                    declaracionId={editData.id}
                    onReporte={(filas) => {
                      if (!filas || filas.length === 0) return;
                      setEditData((prev) => {
                        const anexo = recalcularAnexoA(aplicarVentas607AAnexo(prev.anexo_a, filas));
                        const it1base = aplicarVentas607AIt1Base(prev.it1, filas);
                        const it1 = syncIt1DesdeAnexo(it1base, anexo);
                        return { ...prev, anexo_a: anexo, it1 };
                      });
                      toast.info("Anexo A e IT-1 actualizados con los datos de Ventas (607)");
                    }}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo Imprimir (IT-1 o Anexo) */}
      <Dialog open={!!printData} onOpenChange={(o) => !o && setPrintData(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="imprimir-dialog">
          <DialogHeader className="dgii-no-print">
            <DialogTitle>
              {printData?.tipo === "it1" ? "Documento IT-1" : "Anexo A"} — {printData && formatPeriodo(printData.decl.periodo)}
            </DialogTitle>
            <DialogDescription>Vista del documento para impresión. Pulsa "Imprimir" para enviarlo a la impresora.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 dgii-no-print">
            <Button variant="outline" onClick={() => setPrintData(null)}>Cerrar</Button>
            <Button onClick={handlePrint} data-testid="print-button">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </div>
          {printData?.tipo === "it1" && (
            <FormularioIT1 data={printData.decl.it1} onChange={() => { }} readOnly />
          )}
          {printData?.tipo === "anexo" && (
            <FormularioAnexoA data={printData.decl.anexo_a} onChange={() => { }} readOnly />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar declaración?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la declaración del período {deleteTarget && formatPeriodo(deleteTarget.periodo)}. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleEliminar} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-eliminar">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
